import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import taskConfigService from '../services/taskConfig.service';
import { createDedupedCache, type DedupedCache } from '../utils/dedupedCache';
import type { OrgConfigOut } from '../types/taskConfig.types';

/**
 * Per-org workflow configuration cache.
 *
 * Загружается лениво через `useOrgWorkflow(orgId).ensureLoaded()`.
 * TTL 60 секунд (mirror backend cache — handoff §2.4).
 * Dedup concurrent fetch'ей через `createDedupedCache` (Issue #3).
 * Инвалидируется руками после `saveConfig`/`rollback` или 422 `custom_fields:*`.
 *
 * Backend PG NOTIFY на publish не доходит до фронта в Phase 1.
 * Phase 2: подключить через `SharedWebSocket` для cross-tab инвалидации.
 */

const TTL_MS = 60_000;

interface OrgWorkflowContextValue {
  /** Синхронный lookup: возвращает кэшированный конфиг или `null`. */
  configFor: (orgId: number) => OrgConfigOut | null;
  /** Lazy-load: fetch если нет в кэше или expired. Dedup concurrent вызовы. */
  ensureLoaded: (orgId: number) => Promise<OrgConfigOut>;
  /** Форс-refetch — для ручной кнопки «Обновить». */
  refetch: (orgId: number) => Promise<OrgConfigOut>;
  /** Сбросить кэш для org — вызывается после publish/rollback/422 not_in_enum. */
  invalidate: (orgId: number) => void;
  /** `true`, если сейчас fetch in-flight для org. */
  loadingFor: (orgId: number) => boolean;
  /** Последняя ошибка fetch'а для org. */
  errorFor: (orgId: number) => Error | null;
  /** Подписка на изменения кэша. Возвращает unsubscribe. */
  subscribe: (listener: (orgId: number | null) => void) => () => void;
}

const OrgWorkflowContext = createContext<OrgWorkflowContextValue | null>(null);

export function OrgWorkflowProvider({ children }: { children: React.ReactNode }) {
  const cacheRef = useRef<DedupedCache<number, OrgConfigOut> | null>(null);
  if (!cacheRef.current) {
    cacheRef.current = createDedupedCache<number, OrgConfigOut>({
      fetcher: (orgId) => taskConfigService.getConfig(orgId),
      ttlMs: TTL_MS,
    });
  }

  // Подписка — per-hook, не в Provider. Избегает глобального re-render'а всех
  // consumer'ов, когда меняется данные для конкретной org.
  const subscribe = useCallback((listener: (orgId: number | null) => void) => {
    return cacheRef.current!.subscribe(listener as (key: number) => void);
  }, []);
  const configFor = useCallback((orgId: number) => cacheRef.current!.get(orgId), []);
  const ensureLoaded = useCallback((orgId: number) => cacheRef.current!.ensureLoaded(orgId), []);
  const refetch = useCallback((orgId: number) => cacheRef.current!.refetch(orgId), []);
  const invalidate = useCallback((orgId: number) => cacheRef.current!.invalidate(orgId), []);
  const loadingFor = useCallback((orgId: number) => cacheRef.current!.isLoading(orgId), []);
  const errorFor = useCallback((orgId: number) => cacheRef.current!.errorFor(orgId), []);

  const value = useMemo<OrgWorkflowContextValue>(
    () => ({ configFor, ensureLoaded, refetch, invalidate, loadingFor, errorFor, subscribe }),
    [configFor, ensureLoaded, refetch, invalidate, loadingFor, errorFor, subscribe],
  );

  return <OrgWorkflowContext.Provider value={value}>{children}</OrgWorkflowContext.Provider>;
}

export function useOrgWorkflow(): OrgWorkflowContextValue {
  const ctx = useContext(OrgWorkflowContext);
  if (!ctx) {
    throw new Error('useOrgWorkflow must be used inside <OrgWorkflowProvider>');
  }
  return ctx;
}

/**
 * Удобный hook для компонента, который всегда нуждается в конфиге конкретной org.
 *
 * ```tsx
 * const { config, loading, error, reload } = useOrgWorkflowFor(task.organization_id);
 * if (loading) return <Spinner />;
 * if (error) return <ErrorState onRetry={reload} />;
 * if (!config) return null; // orgId был null
 * ```
 */
export function useOrgWorkflowFor(orgId: number | null | undefined): {
  config: OrgConfigOut | null;
  loading: boolean;
  error: Error | null;
  reload: () => Promise<void>;
} {
  const { configFor, ensureLoaded, refetch, loadingFor, errorFor, subscribe } = useOrgWorkflow();
  const [, tick] = useState(0);

  useEffect(() => {
    if (orgId == null) return;
    void ensureLoaded(orgId).catch(() => {});
    // Re-render только если обновился ИМЕННО этот orgId — никаких лишних
    // перерендеров других consumer'ов на неrelated org-fetch'и.
    return subscribe((key) => {
      if (key === orgId || key === null) tick((t) => t + 1);
    });
  }, [orgId, ensureLoaded, subscribe]);

  const reload = useCallback(async () => {
    if (orgId == null) return;
    await refetch(orgId).catch(() => {});
  }, [orgId, refetch]);

  return {
    config: orgId != null ? configFor(orgId) : null,
    loading: orgId != null ? loadingFor(orgId) : false,
    error: orgId != null ? errorFor(orgId) : null,
    reload,
  };
}
